import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import Cookies from 'js-cookie';

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token') || null);
  const [loading, setLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutCounter, setTimeoutCounter] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();

  const SESSION_TIMEOUT = 20 * 60 * 1000;
  const WARNING_TIME = 5 * 60 * 1000;
  const REFRESH_CHECK_INTERVAL = 30 * 1000;
  const REFRESH_THRESHOLD = 60 * 1000;

  const timeoutWarningTimer = useRef(null);
  const countdownInterval = useRef(null);
  const inactivityTimer = useRef(null);
  const refreshInterval = useRef(null);
  const isRefreshing = useRef(false);
  const refreshSubscribers = useRef([]);
  const refreshPromise = useRef(null);

  const parseJwt = (token) => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(base64));
      let role = 'user';
      if (decoded.scope) {
        if (decoded.scope.includes('FULL')) role = 'admin';
        else if (decoded.scope.includes('DELETE')) role = 'manager';
      }
      return { ...decoded, role };
    } catch (e) {
      return null;
    }
  };

  const handleLogout = useCallback(async (isTimeout = false) => {
    // Clear all timers and intervals
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (refreshInterval.current) clearInterval(refreshInterval.current);
    if (timeoutWarningTimer.current) clearTimeout(timeoutWarningTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);

    try {
      await api.post('/logout', {}, { withCredentials: true });
    } catch (error) {
      console.error('Error logging out:', error);
    }
  
    // Reset all state
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    Cookies.remove('companyId');
    setAccessToken(null);
    setUser(null);
    setShowTimeoutWarning(false); // Explicitly hide the modal
    delete api.defaults.headers.common['Authorization'];

    if (isTimeout) {
      setSessionExpired(true);
    }
    
    // Reset refs
    refreshSubscribers.current = [];
    isRefreshing.current = false;
    refreshPromise.current = null;
    
  }, []);

  // Set up global access after handleLogout is defined
  useEffect(() => {
    window.authContext = { handleLogout };
    return () => delete window.authContext;
  }, [handleLogout]);

  const resetInactivityTimer = useCallback((userInitiated = false) => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (timeoutWarningTimer.current) clearTimeout(timeoutWarningTimer.current);
    
    // Only reset the countdown if the user explicitly continues the session
    if (userInitiated) {
      if (countdownInterval.current) clearInterval(countdownInterval.current);
      setShowTimeoutWarning(false);
    }
  
    inactivityTimer.current = setTimeout(() => handleLogout(true), SESSION_TIMEOUT);
    
    timeoutWarningTimer.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setTimeoutCounter(WARNING_TIME / 1000);
      countdownInterval.current = setInterval(() => {
        setTimeoutCounter(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval.current);
            handleLogout(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT - WARNING_TIME);
  }, [SESSION_TIMEOUT, WARNING_TIME, handleLogout]);

  useEffect(() => {
    const handleActivity = (event) => {
      
      if (!showTimeoutWarning || event.type === 'keypress') {
        resetInactivityTimer();
      }
    };
  
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, handleActivity));
    resetInactivityTimer();
  
    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (timeoutWarningTimer.current) clearTimeout(timeoutWarningTimer.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
  }, [resetInactivityTimer, showTimeoutWarning]);


  const refreshAccessToken = useCallback(async () => {
    if (isRefreshing.current && refreshPromise.current) {
      return refreshPromise.current;
    }

    isRefreshing.current = true;
    refreshPromise.current = new Promise(async (resolve, reject) => {
      try {
        const response = await api.post('/refresh-token', {}, { withCredentials: true });
        const { access_token } = response.data;
        const decoded = parseJwt(access_token);

        if (!decoded) {
          throw new Error('Invalid token');
        }

        // Check if role changed
        if (user?.role && user.role !== decoded.role) {
          await handleLogout();
          return reject('Role changed');
        }

        setAccessToken(access_token);
        localStorage.setItem('access_token', access_token);
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        // Notify all subscribers
        refreshSubscribers.current.forEach(cb => cb(access_token));
        refreshSubscribers.current = [];
        resolve(access_token);
      } catch (error) {
        console.error('Failed to refresh token:', error);
        await handleLogout();
        reject(error);
      } finally {
        isRefreshing.current = false;
        refreshPromise.current = null;
      }
    });

    return refreshPromise.current;
  }, [user?.role, handleLogout]);


  // Set auth header when token changes
  useEffect(() => {
    if (accessToken) {
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [accessToken]);

  // Initial auth check
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          const decoded = parseJwt(storedToken);

          if (!decoded || !parsedUser.userName) {
            throw new Error('Invalid stored credentials');
          }

          // Check if token is expired
          const now = Date.now() / 1000;
          if (decoded.exp < now) {
            // Token expired, try to refresh
            try {
              await refreshAccessToken();
            } catch (error) {
              await handleLogout();
            }
          } else {
            setUser({ ...parsedUser, role: decoded.role });
            setAccessToken(storedToken);
          }
        } catch (error) {
          console.error('Error initializing auth:', error);
          await handleLogout();
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, [handleLogout, refreshAccessToken]);

  // Setup token refresh interval
  useEffect(() => {
    if (!accessToken) return;
  
    const checkAndRefreshToken = async () => {
      const decoded = parseJwt(accessToken);
      if (!decoded) return;
  
      const now = Date.now();
      const expiresAt = decoded.exp * 1000;
      const timeUntilExpiry = expiresAt - now;
  
      if (timeUntilExpiry < REFRESH_THRESHOLD) {
        try {
          await refreshAccessToken();
        } catch (error) {
          console.error('Auto-refresh failed:', error);
        }
      }
    };
  
    // Initial check
    checkAndRefreshToken();
    
    // Set up periodic checking
    const intervalId = setInterval(checkAndRefreshToken, REFRESH_CHECK_INTERVAL);
    refreshInterval.current = intervalId;
  
    return () => {
      clearInterval(intervalId);
    };
  }, [accessToken, refreshAccessToken, REFRESH_CHECK_INTERVAL, REFRESH_THRESHOLD]);

  const signup = async (name, email, password, role = 'role_user') => {
    try {
      const { data } = await api.post('/signup', { name, email, password, role });
      
      
      return { 
        success: true, 
        message: data.message ||'Account created successfully! Please log in to continue.' 
      };
    } catch (error) {
      console.error('Signup failed:', error);
      let errorMessage = 'Signup failed. Please try again.';
      
      if (error.response) {
        if (error.response.status === 409) {
          errorMessage = 'This email is already registered.';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      return { 
        success: false, 
        message: errorMessage 
      };
    }
  };

  const login = async (email, password) => {
    try {
      // Clear any existing auth state
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      Cookies.remove('companyId');
      delete api.defaults.headers.common['Authorization'];
  
      setSessionExpired(false);
  
      const basicAuth = 'Basic ' + btoa(`${email}:${password}`);
      const response = await api.post('/sign-in', {}, { 
        headers: { 
          Authorization: basicAuth,
          'X-Requested-With': 'XMLHttpRequest'  
        },
        withCredentials: true
      });
      
      if (!response.data?.access_token) {
        throw new Error('Invalid response from server');
      }
  
      const { access_token, user_name } = response.data;
      const decoded = parseJwt(access_token);
  
      if (!decoded) {
        throw new Error('Invalid token received');
      }
  
      const userData = {
        userName: user_name,
        role: decoded.role
      };
  
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setAccessToken(access_token);
      setUser(userData);
      
      navigate(decoded.role === 'admin' ? '/admin' : '/userdashboard');
      return { success: true };
    } catch (error) {
      // Force clear all auth state on failure
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      Cookies.remove('companyId');
      delete api.defaults.headers.common['Authorization'];
      setAccessToken(null);
      setUser(null);
  
      console.error('Login error:', error);
  
      // Handle network errors (server offline)
      if (error.isNetworkError || error.response?.status === 0) {
        return { 
          success: false, 
          message: 'Cannot connect to the server. The server might be offline or your network connection is down. Please try again later.',
          isNetworkError: true,
          status: 0
        };
      }
  
      // Handle specific HTTP errors
      let errorMessage = 'Login failed. Please try again.';
      let status = error.response?.status;
      
      if (status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (status === 403) {
        errorMessage = 'Account disabled. Please contact support.';
      } else if (status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.isAuthError) {
        errorMessage = error.message || 'Authentication failed';
      }
  
      return { 
        success: false, 
        message: errorMessage,
        status: status 
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await api.post('/forgot-password', { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Forgot password failed:', error);
      return { success: false, message: error.response?.data?.message || 'Request failed' };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        loading,
        login,
        signup,
        forgotPassword,
        refreshAccessToken,
        logout: handleLogout,
        setShowTimeoutWarning,
        resetUser: () => {
          localStorage.removeItem('user');
          setUser(null);
        },
        isAuthenticated: !!user && !!accessToken,
        showTimeoutWarning,
        timeoutCounter,
        sessionExpired,
        setSessionExpired,
        extendSession: () => {
          resetInactivityTimer(true); 
        }
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}; 